import { useMemo } from "react"
import { minBy, sumBy } from "lodash"
import { groupBy } from "~/utils/tsUtils"

import { Card, CardContent } from "~/components/reusable/Card"
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
} from "~/components/reusable/Table"
import { useCollectionGroups, useGetCollections } from "~/hooks/useCollections"
import { useSkuStore } from "~/providers/sku-store-provider"
import { startOfTodayUTC } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { CollectionLink } from "~/components/reusable/CollectionLink"

type Props = {
  variants: IVariantSummary[]
}

// Important note: This is only used for Carhartt WIP, but could be used for other organizations in the future.
const CollectionStockLevel = ({ variants }: Props) => {
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const { data: collections, isLoading: collectionsLoading } = useGetCollections()
  const { data: collectionGroups, isLoading: groupsLoading } = useCollectionGroups()

  const groupedStockData = useMemo(() => {
    if (!collections || collections.length === 0) return []

    const today = startOfTodayUTC()

    // Filter to only collections that have started
    const startedCollections = collections.filter((c) => !c.startDate || c.startDate <= today)

    // Create map of collection ID to collection data
    const collectionMap = new Map(
      startedCollections.map((c) => [c.id, { ...c, groupName: c.groupName ?? null }]),
    )

    // Track which collection each variant belongs to (latest by startDate)
    const variantToCollectionMap = new Map<string, number>()

    for (const variant of variants) {
      if (!variant.collections || variant.collections.length === 0) continue

      let latestCollection: { id: number; startDate: Date | null } | null = null

      for (const col of variant.collections) {
        const collectionId = typeof col.id === "number" ? col.id : Number(col.id)
        const collection = collectionMap.get(collectionId)
        if (!collection) continue // Skip if collection hasn't started
        if (collection.name.toLowerCase().includes("unknown")) continue // Skip "Unknown" collections

        const startDate = collection.startDate

        if (
          !latestCollection ||
          (startDate && (!latestCollection.startDate || startDate > latestCollection.startDate))
        ) {
          latestCollection = { id: collectionId, startDate }
        }
      }

      if (latestCollection) {
        variantToCollectionMap.set(variant.variantId, latestCollection.id)
      }
    }

    // Sum stock levels per collection (filtered by warehouse)
    const collectionStockMap = new Map<number, number>()

    for (const variant of variants) {
      const collectionId = variantToCollectionMap.get(variant.variantId)
      if (!collectionId) continue

      const stockLevel = selectedWarehouseId
        ? (variant.stockLevelDetails?.find((s) => s.warehouseId === selectedWarehouseId)
            ?.closingStock ?? 0)
        : (variant.stockLevel ?? 0)

      const currentStock = collectionStockMap.get(collectionId) ?? 0
      collectionStockMap.set(collectionId, currentStock + stockLevel)
    }

    // Build collection data with stock
    const collectionsWithStock = Array.from(collectionStockMap.entries())
      .map(([collectionId, stockLevel]) => {
        const collection = collectionMap.get(collectionId)
        if (!collection) return null

        return {
          id: collection.id,
          name: collection.name,
          collectionType: collection.collectionType,
          collectionGroupId: collection.collectionGroupId,
          groupName: collection.groupName,
          startDate: collection.startDate,
          stockLevel,
        }
      })
      .filter((item) => item !== null)

    // Check if there are any collection groups with stock
    const grouped = groupBy(collectionsWithStock, (c) => c.collectionGroupId?.toString() ?? "null")
    const hasGroupsWithStock =
      collectionGroups &&
      collectionGroups.some((group) => {
        const collections = grouped[group.id.toString()] || []
        return collections.length > 0
      })

    const result = []

    // Only group if there are groups with stock
    if (hasGroupsWithStock && collectionGroups) {
      // Add collection groups
      for (const group of collectionGroups) {
        const collections = grouped[group.id.toString()] || []
        if (collections.length === 0) continue

        const earliestCollection = minBy(collections, (c) => c.startDate?.getTime() ?? Infinity)
        result.push({
          id: group.id,
          name: group.name,
          collectionType: collections[0]?.collectionType,
          isGroup: true,
          startDate: earliestCollection?.startDate ?? null,
          stockLevel: sumBy(collections, "stockLevel"),
        })
      }

      // Add ungrouped collections
      const ungroupedCollections = grouped["null"] || []
      for (const collection of ungroupedCollections) {
        result.push({
          id: collection.id,
          name: collection.name,
          collectionType: collection.collectionType,
          isGroup: false,
          startDate: collection.startDate,
          stockLevel: collection.stockLevel,
        })
      }
    } else {
      // No groups with stock, show individual collections
      for (const collection of collectionsWithStock) {
        result.push({
          id: collection.id,
          name: collection.name,
          collectionType: collection.collectionType,
          isGroup: false,
          startDate: collection.startDate,
          stockLevel: collection.stockLevel,
        })
      }
    }

    // Sort by startDate descending (most recent first) and limit to top 15
    return result
      .sort((a, b) => (b.startDate?.getTime() ?? 0) - (a.startDate?.getTime() ?? 0))
      .slice(0, 15)
  }, [variants, collections, collectionGroups, selectedWarehouseId])

  const isLoading = collectionsLoading || groupsLoading

  return (
    <Card>
      <CardContent className="max-h-[168px] overflow-y-auto p-2">
        {isLoading ? (
          <LoadingOverlay />
        ) : groupedStockData.length > 0 ? (
          <SimpleTable>
            <SimpleTableHeader className="sticky top-0">
              <SimpleTableRow>
                <SimpleTableHead>Collection</SimpleTableHead>
                <SimpleTableHead>
                  <div className="flex items-center gap-1">
                    Stock Level
                    <InfoTooltip
                      body={
                        <div className="max-w-xs">
                          Shows current stock levels grouped by collection. Each variant is counted
                          once in its most recent started collection.
                        </div>
                      }
                    />
                  </div>
                </SimpleTableHead>
              </SimpleTableRow>
            </SimpleTableHeader>
            <SimpleTableBody>
              {groupedStockData.map((item) => (
                <SimpleTableRow key={`${item.isGroup ? "group" : "col"}-${item.id}`}>
                  <SimpleTableCell>
                    <CollectionLink
                      collectionId={item.id}
                      collectionName={item.name}
                      collectionType={item.collectionType}
                      isGroup={item.isGroup}
                    />
                  </SimpleTableCell>
                  <SimpleTableCell>{formatNumber(item.stockLevel, 0)}</SimpleTableCell>
                </SimpleTableRow>
              ))}
            </SimpleTableBody>
          </SimpleTable>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-fg-muted text-sm">No collection stock data</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default CollectionStockLevel
